import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import StatusPill from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { endpoints } from "@/lib/api";
import { fmtMoney, fmtDate, todayISO } from "@/lib/utils-format";
import { generateDocPDF } from "@/lib/pdf";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Download, Send, CheckCircle2, ArrowRight } from "lucide-react";

const STATUSES = ["Draft", "Sent", "Approved", "Declined", "Expired"];

const newLineItemId = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `li-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);

export default function Estimates() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [search] = useSearchParams();
  const navigate = useNavigate();

  function emptyForm() {
    return {
      number: `EST-${Math.floor(Math.random()*9000)+1000}`,
      client_id: "", client_name: "", address: "",
      service: "", status: "Draft",
      line_items: [{ _lid: newLineItemId(), description: "", qty: 1, unit_price: 0, amount: 0 }],
      subtotal: 0, tax_rate: 8.25, tax: 0, discount: 0, deposit: 0, total: 0, balance: 0,
      valid_until: "", notes: "",
    };
  }

  const load = useCallback(async () => {
    const [a, b] = await Promise.all([endpoints.list("estimates"), endpoints.list("clients")]);
    setItems(a.data); setClients(b.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (search.get("new") === "1") {
      const clientId = search.get("client");
      const c = clientId ? clients.find(x => x.id === clientId) : null;
      setEditing(null);
      setForm({ ...emptyForm(), client_id: c?.id || "", client_name: c ? `${c.first_name} ${c.last_name}` : "", address: c ? `${c.street}, ${c.city}, ${c.state} ${c.zip}` : "" });
      setOpen(true);
    }
  }, [search, clients]);

  const recalc = (f) => {
    const subtotal = f.line_items.reduce((s, li) => s + Number(li.qty||0) * Number(li.unit_price||0), 0);
    const tax = +(subtotal * (Number(f.tax_rate||0)/100)).toFixed(2);
    const total = +(subtotal + tax - Number(f.discount||0)).toFixed(2);
    return { ...f, subtotal, tax, total, balance: total - Number(f.deposit||0) };
  };

  const updateItem = (idx, k, v) => {
    const li = [...form.line_items];
    li[idx] = { ...li[idx], [k]: k === "description" ? v : Number(v||0) };
    li[idx].amount = li[idx].qty * li[idx].unit_price;
    setForm(recalc({ ...form, line_items: li }));
  };
  const addLine = () => setForm({ ...form, line_items: [...form.line_items, { _lid: newLineItemId(), description: "", qty: 1, unit_price: 0, amount: 0 }] });
  const removeLine = (i) => setForm(recalc({ ...form, line_items: form.line_items.filter((_, idx) => idx !== i) }));

  const startNew = () => { setEditing(null); setForm(emptyForm()); setOpen(true); };
  const startEdit = (e) => { setEditing(e); setForm({ ...emptyForm(), ...e }); setOpen(true); };

  const save = async () => {
    const data = recalc(form);
    try {
      if (editing) await endpoints.update("estimates", editing.id, data);
      else await endpoints.create("estimates", data);
      toast.success("Estimate saved");
      setOpen(false); load();
    } catch { toast.error("Save failed"); }
  };

  const send = async (e) => { await endpoints.update("estimates", e.id, { ...e, status: "Sent" }); toast.success("Estimate marked as sent"); load(); };
  const approve = async (e) => { await endpoints.update("estimates", e.id, { ...e, status: "Approved" }); toast.success("Estimate approved"); load(); };
  const remove = async (id) => { if(!window.confirm("Delete?"))return; await endpoints.remove("estimates", id); toast.success("Deleted"); load(); };

  const convertToJob = async (e) => {
    await endpoints.create("jobs", {
      client_id: e.client_id, client_name: e.client_name, address: e.address,
      service: e.service, status: "Pending", price: e.total,
      date: todayISO(), time: "09:00",
      priority: "Normal", crew: "Crew A", equipment: "", materials: "", notes: `From ${e.number}`, estimate_id: e.id,
    });
    toast.success("Converted to job");
    navigate("/jobs");
  };

  const onClientChange = (id) => {
    const c = clients.find(x => x.id === id);
    setForm({ ...form, client_id: id, client_name: c ? `${c.first_name} ${c.last_name}` : "", address: c ? `${c.street}, ${c.city}, ${c.state} ${c.zip}` : "" });
  };

  return (
    <Layout title="Estimates" subtitle={`${items.length} estimates`}>
      <div className="flex justify-end mb-4"><Button onClick={startNew} data-testid="estimates-new"><Plus className="size-4 mr-1.5"/>New Estimate</Button></div>
      <Card className="rounded-xl overflow-hidden">
        <div className="overflow-x-auto"><table className="w-full text-sm" data-testid="estimates-table">
          <thead className="bg-muted/40"><tr className="text-xs uppercase tracking-wider text-muted-foreground text-left">
            <th className="p-4 font-semibold">Number</th><th className="p-4 font-semibold">Customer</th>
            <th className="p-4 font-semibold">Service</th><th className="p-4 font-semibold">Total</th>
            <th className="p-4 font-semibold">Status</th><th className="p-4 font-semibold">Valid Until</th><th className="p-4 font-semibold w-44">Actions</th>
          </tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan="7" className="p-10 text-center text-muted-foreground">No estimates yet.</td></tr>}
            {items.map(e => (
              <tr key={e.id} className="border-t border-border hover:bg-muted/30">
                <td className="p-4 font-mono text-xs font-semibold">{e.number}</td>
                <td className="p-4">{e.client_name}</td>
                <td className="p-4">{e.service}</td>
                <td className="p-4 font-mono font-semibold">{fmtMoney(e.total)}</td>
                <td className="p-4"><StatusPill status={e.status}/></td>
                <td className="p-4 text-xs text-muted-foreground">{fmtDate(e.valid_until)}</td>
                <td className="p-4">
                  <div className="flex items-center gap-1">
                    <Button size="icon" variant="ghost" className="size-7" onClick={()=>startEdit(e)} title="Edit"><Pencil className="size-3.5"/></Button>
                    <Button size="icon" variant="ghost" className="size-7" onClick={()=>generateDocPDF({kind:"ESTIMATE",doc:e})} title="Download"><Download className="size-3.5"/></Button>
                    {e.status === "Draft" && <Button size="icon" variant="ghost" className="size-7" onClick={()=>send(e)} title="Send"><Send className="size-3.5"/></Button>}
                    {e.status === "Sent" && <Button size="icon" variant="ghost" className="size-7" onClick={()=>approve(e)} title="Approve"><CheckCircle2 className="size-3.5"/></Button>}
                    {e.status === "Approved" && <Button size="icon" variant="ghost" className="size-7" onClick={()=>convertToJob(e)} title="To Job"><ArrowRight className="size-3.5"/></Button>}
                    <Button size="icon" variant="ghost" className="size-7 text-destructive" onClick={()=>remove(e.id)}><Trash2 className="size-3.5"/></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="estimate-form">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Edit Estimate" : "New Estimate"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4 mt-2">
            <div className="space-y-1.5"><Label>Estimate #</Label><Input value={form.number} onChange={(e)=>setForm({...form, number:e.target.value})}/></div>
            <div className="space-y-1.5"><Label>Customer</Label>
              <Select value={form.client_id} onValueChange={onClientChange}>
                <SelectTrigger data-testid="estimate-client"><SelectValue placeholder="Select client"/></SelectTrigger>
                <SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 col-span-2"><Label>Address</Label><Input value={form.address} onChange={(e)=>setForm({...form, address:e.target.value})}/></div>
            <div className="space-y-1.5"><Label>Service</Label><Input value={form.service} onChange={(e)=>setForm({...form, service:e.target.value})}/></div>
            <div className="space-y-1.5"><Label>Status</Label>
              <Select value={form.status} onValueChange={(v)=>setForm({...form, status:v})}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Valid Until</Label><Input type="date" value={form.valid_until} onChange={(e)=>setForm({...form, valid_until:e.target.value})}/></div>
            <div className="space-y-1.5"><Label>Tax Rate (%)</Label><Input type="number" step="0.01" value={form.tax_rate} onChange={(e)=>setForm(recalc({...form, tax_rate:Number(e.target.value)}))}/></div>
          </div>

          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm font-semibold">Line Items</Label>
              <Button size="sm" variant="outline" onClick={addLine}><Plus className="size-3.5 mr-1"/>Add Line</Button>
            </div>
            <div className="space-y-2">
              {form.line_items.map((li, i) => (
                <div key={li._lid || `idx-${i}`} className="grid grid-cols-12 gap-2 items-center" data-testid={`estimate-line-${i}`}>
                  <Input className="col-span-6" placeholder="Description" value={li.description} onChange={(e)=>updateItem(i,"description",e.target.value)}/>
                  <Input className="col-span-1" type="number" value={li.qty} onChange={(e)=>updateItem(i,"qty",e.target.value)}/>
                  <Input className="col-span-2" type="number" step="0.01" value={li.unit_price} onChange={(e)=>updateItem(i,"unit_price",e.target.value)}/>
                  <div className="col-span-2 font-mono text-sm text-right">{fmtMoney(li.amount)}</div>
                  <Button size="icon" variant="ghost" className="col-span-1 size-8 text-destructive" onClick={()=>removeLine(i)}><Trash2 className="size-3.5"/></Button>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mt-4 items-end">
            <div className="space-y-1.5"><Label>Discount ($)</Label><Input type="number" step="0.01" value={form.discount} onChange={(e)=>setForm(recalc({...form, discount:Number(e.target.value)}))}/></div>
            <div className="space-y-1.5"><Label>Deposit ($)</Label><Input type="number" step="0.01" value={form.deposit} onChange={(e)=>setForm(recalc({...form, deposit:Number(e.target.value)}))}/></div>
            <div className="col-span-2 space-y-1.5"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e)=>setForm({...form, notes:e.target.value})}/></div>
            <div className="col-span-2 flex justify-end items-center gap-6 bg-muted/30 rounded-lg p-4">
              <div className="text-xs text-muted-foreground">Subtotal: <span className="text-foreground font-semibold">{fmtMoney(form.subtotal)}</span></div>
              <div className="text-xs text-muted-foreground">Tax: <span className="text-foreground font-semibold">{fmtMoney(form.tax)}</span></div>
              <div className="font-display font-bold text-lg">Total: {fmtMoney(form.total)}</div>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="estimate-save">Save Estimate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
